// CrashCoursesPage.jsx
// Responsive, production-ready React + Tailwind landing page for Rankridge Crash Courses
// Uses framer-motion (animations) and lucide-react (icons). Optional but recommended.
// Install (if needed): npm i framer-motion lucide-react
// Drop this file in your React app (e.g., src/pages/CrashCoursesPage.jsx) and import <CrashCoursesPage />

import React from "react";
import { motion } from "framer-motion";
import { ShorttermCourse } from "../data";
import {
  BookOpenCheck,
  GraduationCap,
  Clock,
  FlaskConical,
  Atom,
  TestTubes,
  FileStack,
  CheckCircle2,
  Users,
  Building2,
  PhoneCall,
  Sparkles,
  Trophy,
  BadgeCheck,
  ArrowRight,
  Calendar,
  Activity,
} from "lucide-react";
import CommonImageSliderRanks from "../CommonsliderRanks";
import { studentsRanks } from "../data";
import HeroVideo from "../seometa/HeroVideo";

export default function CrashCoursesPage() {
  const programs = [
    {
      key: "eapcet",
      title: "EAMCET (EAPCET) Crash Course",
      bullets: [
        "TS & AP specific syllabus coverage in 45–60 days",
        "Daily practice sheets, chapter tests, and full-length mocks",
        "Speed-boost shortcuts & accuracy drills",
      ],
      icon: <FlaskConical className="h-5 w-5" />,
    },
    {
      key: "iitjee",
      title: "IIT-JEE (Mains & Advanced) Crash Course",
      bullets: [
        "Concept mapping for Physics, Chemistry, Maths",
        "Rank-focused workbooks & test series",
        "Mentorship with IIT/NIT alumni",
      ],
      icon: <Atom className="h-5 w-5" />,
    },
    {
      key: "neet",
      title: "NEET Crash Course",
      bullets: [
        "NCERT-highweight chapters prioritized",
        "Daily MCQs + weekly grand tests",
        "Concept-first teaching with shortcut logic",
      ],
      icon: <TestTubes className="h-5 w-5" />,
    },
    {
      key: "bitsat",
      title: "BITSAT Speed Program",
      bullets: [
        "Logical Reasoning & English Pro modules",
        "Time-optimized practice for speed & accuracy",
        "Pattern-based mock exams",
      ],
      icon: <BookOpenCheck className="h-5 w-5" />,
    },
  ];

  const features = [
    {
      icon: <Clock className="h-6 w-6" />,
      title: "45–60 Day Sprints",
      text: "Maximum results in minimum time with focused roadmaps.",
    },
    {
      icon: <GraduationCap className="h-6 w-6" />,
      title: "Elite Faculty",
      text: "15–25 years of EAMCET/JEE/NEET teaching experience.",
    },
    {
      icon: <FileStack className="h-6 w-6" />,
      title: "Crash Packs",
      text: "Concise modules, DPPs, formula handbooks, and mocks.",
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "1:1 Mentoring",
      text: "Personalized strategies to lift speed, accuracy, and confidence.",
    },  {
    icon: <Calendar className="h-6 w-6 text-emerald-600" />,
    title: "Parent–Faculty Connect",
    text: "Regular PTMs, digital dashboard access, and counselling sessions for exam stress.",
  },
  {
    icon: <Activity className="h-6 w-6 text-emerald-600" />,
    title: "Performance Tracking & Analytics",
    text: "Weekly subject-wise reports, AI-driven analysis, and transparent scorecards.",
  }


  ];

  const materials = [
    {
      title: "EAPCET Crash Material",
      items: ["TS & AP syllabi", "Chapter-wise MCQs", "Grand Tests"],
    },
    {
      title: "IIT-JEE Rank Modules",
      items: ["Mains & Adv workbooks", "Test series", "Shortcut sheets"],
    },
    {
      title: "NEET Crash Pack",
      items: ["NCERT-highweight", "Daily MCQs", "Weekly grand tests"],
    },
    {
      title: "BITSAT Speed Kit",
      items: ["Logical Reasoning", "English Pro", "Pattern mocks"],
    },
  ];

  const stats = [
    { label: "Selections (5 yrs)", value: "1400+" },
    { label: "Accuracy vs Exam", value: "~90%" },
    { label: "Batch Duration", value: "45–60 Days" },
    { label: "Mentors", value: "IITs / NITs / AIIMS" },
  ];

  return (
    <div className="bg-white text-neutral-900">
      {/* Top Contact Bar */}
      <div className="bg-teal-600 text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 text-sm">
          <p className="flex items-center gap-2"><BadgeCheck className="h-4 w-4"/>Admissions Open – 2026 Crash Course Batches</p>
          <a href="tel:+919959271353" className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 hover:bg-white/20">
            <PhoneCall className="h-4 w-4"/> +91 99 59 271 353
          </a>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
          <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl font-extrabold text-center leading-tight sm:text-3xl md:text-3xl"
            >
              Best EAMCET (EAPCET), IIT-JEE, NEET & BITSAT
              Short-Term Coaching  in Hyderabad
            </motion.h1>
        {/* Decorative gradient blob 
        <div className="pointer-events-none absolute -left-20 -top-24 h-80 w-80 rounded-full bg-teal-200 blur-3xl opacity-60" />
        <div className="pointer-events-none absolute -right-20 top-1/4 h-80 w-80 rounded-full bg-emerald-100 blur-3xl opacity-60" />
*/}
        <div className="mx-auto grid  grid-cols-1 items-center gap-10 px-4 py-1 md:grid-cols-2 md:py-2">
          <div>
          
            <p className="mt-6 text-base leading-relaxed text-neutral-600 md:text-lg">
              Rankridge – Hyderabad’s No.1 Crash Course Coaching Center. 45–60 day, result-oriented sprints with expert faculty, focused materials, and proven track record.
            </p>
      
            <div className="mt-8 flex flex-wrap items-center  justify-center gap-3  ">
              <a
                href="tel:+919959271353"
                className="inline-flex items-center gap-2 rounded-2xl bg-teal-600 px-5 py-3 font-semibold text-white shadow-lg shadow-teal-600/20 hover:bg-teal-700"
              >
                <PhoneCall className="h-5 w-5" /> Call Admissions
              </a>
              <a
                href="https://www.rankridge.com"
                target="_blank"
                rel="noreferrer"
                className="inline-flex border border-[#20b2aa] border-2 items-center gap-2 rounded-2xl  px-5 py-3 font-semibold hover:bg-neutral-50"
              >
                Enquiry Now <ArrowRight className="h-5 w-5" />
              </a>
              {/* <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
                <Trophy className="h-4 w-4" /> Proven Results
              </span> */}
            </div>

            {/* Trust points */}
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-2">
              {stats.map((s) => (
                <div key={s.label} className="rounded-2xl border border-neutral-200 bg-white p-4 text-center">
                  <div className="text-xl font-extrabold sm:text-2xl">{s.value}</div>
                  <div className="mt-1 text-xs text-neutral-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero media placeholder */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative"
          >
            <div className="aspect-[4/3] w-full overflow-hidden rounded-3xl border border-neutral-200 bg-neutral-50 shadow-xl">
              {/* Replace this with an <img> or video */}
              <div className="flex h-full w-full items-center justify-center">
                                   <img src={ShorttermCourse[3]} className="w-full rounded-2xl object-contain h-auto"   loading="lagy" />

              
              </div>
            </div>
            <div className="absolute -bottom-4 -left-4 rounded-2xl bg-white p-3 shadow-md ring-1 ring-neutral-200">
              <div className="flex items-center gap-2 text-sm">
                <BadgeCheck className="h-4 w-4 text-teal-600" /> Hyderabad’s Trusted Crash Course Hub
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Programs */}
      <section className=" py-1 bg-[#20b2aa]">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-center text-2xl font-bold sm:text-3xl">Choose Your Program</h2>
          <p className="mx-auto mt-3 max-w-3xl text-center text-black">
            Complete syllabus coverage • Daily practice • Chapter tests • Full-length mocks • Shortcut strategies
          </p>

          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2">
            {programs.map((p) => (
              <div key={p.key} className="group relative rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm transition hover:shadow-lg">
                <div className="flex items-center gap-2 text-teal-700">
                  {p.icon}
                  <h3 className="font-semibold">{p.title}</h3>
                </div>
                <ul className="mt-4 space-y-2 text-sm text-neutral-600">
                  {p.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-600" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <a href="tel:+919959271353" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-neutral-900 px-4 py-2 text-sm font-semibold text-white hover:bg-neutral-800">
                  Enquire Now <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Rankridge */}
      <section className="py-3 mt-4">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid grid-cols-1 items-start gap-10 md:grid-cols-2">
            <div className="border border-[#20b2aa] border-4 rounded-2xl shadow-2xl">
              <h2 className="text-2xl mt-3 font-bold sm:text-2xl mt-2 text-center">Why Choose Rankridge for Short‑Term Coaching?</h2>
             

              <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {features.map((f) => (
                  <div key={f.title} className="rounded-2xl border border-neutral-200 p-5">
                    <div className="flex items-center gap-2 text-teal-700">
                      {f.icon}
                      <h3 className="font-semibold">{f.title}</h3>
                    </div>
                    <p className="mt-2 text-sm text-neutral-600">{f.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Materials */}
            <div>
              <div className="rounded-3xl border border-[#20b2aa] border-4 p-6 shadow-2xl ">
                <h3 className=" text-2xl font-semibold text-center"> Crash Course Materials – Concise & Exam‑Specific</h3>
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {materials.map((m) => (
                    <div key={m.title} className="rounded-2xl bg-neutral-50 p-4">
                      <div className="font-semibold">{m.title}</div>
                      <ul className="mt-2 space-y-1 text-sm text-neutral-600">
                        {m.items.map((it) => (
                          <li key={it} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600"/>{it}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-sm text-neutral-500">Daily quick‑revision sheets & important formula handbooks included.</p>
              </div>

            </div>
          </div>
        </div>

        
              {/* Facilities */}
              <div className="mt-6 rounded-3xl border border-neutral-200 p-6">
                <h3 className=" gap-2 text-2xl text-center justify-items-center font-semibold"> World‑Class Learning & Hostel Facilities</h3>
                <ul className="mt-3  grid grid-cols-1  space-y-2 text-sm text-neutral-600 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 ">
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600"/> AC smart classrooms with digital boards & interactive sessions</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600"/> Separate boys’ & girls’ hostels with CCTV & wardens</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600"/> Nutritious meals, evening snacks & monitored study hours</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600"/> Motivational sessions & wellness programs for stress‑free learning</li>
                   <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600"/> Regular parent updates & performance tracking</li> 
               
              <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600"/>  24/7 Medical Support & Emergency Care For Student Safety </li>
                </ul>
              </div>
      </section>

      {/* Results & Social Proof */}
      <section className="bg-neutral-950 py-16 text-white">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2">
            <div>
              <h2 className="text-2xl font-bold sm:text-3xl">Proven Track Record of Results</h2>
              <p className="mt-3 text-neutral-300">
                Hundreds of students from our crash courses have achieved top ranks in EAMCET, NEET, IIT‑JEE & BITSAT. Our test series have predicted exam patterns with ~90% accuracy.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="rounded-2xl bg-neutral-900 p-5">
                  <div className="text-3xl font-extrabold">Top Ranks</div>
                  <p className="mt-1 text-sm text-neutral-400">Year‑after‑year admissions to top colleges</p>
                </div>
                <div className="rounded-2xl bg-neutral-900 p-5">
                  <div className="text-3xl font-extrabold">Expert Mentors</div>
                  <p className="mt-1 text-sm text-neutral-400">IITs, NITs, AIIMS backed faculty</p>
                </div>
              </div>
            </div>

            {/* Replace with collage / results banner */}
            <div className="rounded-3xl border border-neutral-800 bg-neutral-900 p-6">
              <div className="aspect-[4/3] w-full rounded-2xl bg-neutral-800/60">
                                   <img src={ShorttermCourse[4]} className="w-full rounded-2xl object-contain h-auto"   loading="lagy" />

              </div>
                    <CommonImageSliderRanks images={studentsRanks}/>

            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-2xl font-bold sm:text-3xl">About Rankridge Crash Courses</h2>
          <p className="mt-3 max-w-4xl text-neutral-600">
            Rankridge College is Hyderabad’s most trusted destination for short‑term coaching in EAMCET (EAPCET), IIT‑JEE, NEET, and BITSAT. Programs compress the complete syllabus into 45–60 days of focused preparation, aligned with latest exam trends and powered by concept‑based teaching, shortcut strategies, daily practice tests, and mock exams.
          </p>
          <p className="mt-3 max-w-4xl text-neutral-600">
            Beyond academics, we ensure a supportive environment with digital classrooms, safe hostels, nutritious food, and stress‑free guidance. Parents receive regular performance updates while students benefit from motivational sessions and personalized mentoring.
          </p>
        </div>
      </section>

      {/* Sticky CTA */}
      <div className="sticky bottom-4 z-40 mx-auto max-w-7xl px-4">
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-white p-3 shadow-xl">
          <div className="text-sm">
            <div className="font-semibold">Join Hyderabad’s Most Trusted Crash Course Coaching Center</div>
            <div className="text-neutral-600">Finish strong with focused study, expert guidance, and proven results.</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a href="tel:+919959271353" className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2 font-semibold text-white hover:bg-teal-700">
              <PhoneCall className="h-4 w-4"/> Call Now
            </a>
            <a href="https://www.rankridge.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-neutral-200 px-4 py-2 font-semibold hover:bg-neutral-50">
              rankridge.com <ArrowRight className="h-4 w-4"/>
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-16 border-t border-neutral-200 py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 sm:flex-row">
          <div className="text-center sm:text-left">
            <div className="text-lg font-bold">Rankridge College</div>
            <div className="text-sm text-neutral-600">Crash Courses for EAMCET • IIT‑JEE • NEET • BITSAT</div>
          </div>
          <a href="tel:+919959271353" className="inline-flex items-center gap-2 rounded-xl bg-neutral-900 px-4 py-2 font-semibold text-white hover:bg-neutral-800">
            <PhoneCall className="h-4 w-4"/> +91 99 59 271 353
          </a>
        </div>
      </footer>
    </div>
  );
}
